"use client";

import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/rac/button";
import { cn } from "@/utils/cn";

export type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
	opts?: CarouselOptions;
	plugins?: CarouselPlugin;
	orientation?: "horizontal" | "vertical";
	setApi?: (api: CarouselApi) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0];
	api: ReturnType<typeof useEmblaCarousel>[1];
	scrollPrev: () => void;
	scrollNext: () => void;
	canScrollPrev: boolean;
	canScrollNext: boolean;
	focusCarousel: () => void;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

export function useCarousel() {
	const context = React.useContext(CarouselContext);

	if (!context) {
		throw new Error("useCarousel must be used within a <Carousel />");
	}

	return context;
}

function Carousel({
	orientation = "horizontal",
	opts,
	setApi,
	plugins,
	className,
	children,
	onKeyDown,
	...props
}: React.ComponentProps<"div"> & CarouselProps) {
	const [carouselRef, api] = useEmblaCarousel({ ...opts, axis: orientation === "horizontal" ? "x" : "y" }, plugins);
	const [canScrollPrev, setCanScrollPrev] = React.useState(false);
	const [canScrollNext, setCanScrollNext] = React.useState(false);
	const carouselContainerRef = React.useRef<HTMLDivElement>(null);

	const onSelect = React.useCallback((api: CarouselApi) => {
		if (!api) return;
		setCanScrollPrev(api.canScrollPrev());
		setCanScrollNext(api.canScrollNext());
	}, []);

	const scrollPrev = React.useCallback(() => {
		api?.scrollPrev();
	}, [api]);

	const scrollNext = React.useCallback(() => {
		api?.scrollNext();
	}, [api]);

	const focusCarousel = React.useCallback(() => {
		carouselContainerRef.current?.focus();
	}, []);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				scrollPrev();
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				scrollNext();
			}

			// Call the custom onKeyDown handler if provided
			onKeyDown?.(event);
		},
		[scrollPrev, scrollNext, onKeyDown],
	);

	React.useEffect(() => {
		if (!api || !setApi) return;
		setApi(api);
	}, [api, setApi]);

	React.useEffect(() => {
		if (!api) return;
		onSelect(api);
		api.on("reInit", onSelect);
		api.on("select", onSelect);

		return () => {
			api?.off("reInit", onSelect);
			api?.off("select", onSelect);
		};
	}, [api, onSelect]);

	const contextValue = React.useMemo(
		() => ({
			carouselRef,
			api: api,
			opts,
			orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
			scrollPrev,
			scrollNext,
			canScrollPrev,
			canScrollNext,
			focusCarousel,
		}),
		[carouselRef, api, opts, orientation, scrollPrev, scrollNext, canScrollPrev, canScrollNext, focusCarousel],
	);

	return (
		<CarouselContext.Provider value={contextValue}>
			<div
				ref={carouselContainerRef}
				onKeyDown={handleKeyDown}
				className={cn("relative", className)}
				role="region"
				aria-roledescription="carousel"
				data-slot="carousel"
				tabIndex={0}
				{...props}
			>
				{children}
			</div>
		</CarouselContext.Provider>
	);
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
	const { carouselRef, orientation } = useCarousel();

	return (
		<div ref={carouselRef} className="overflow-hidden h-full" data-slot="carousel-content">
			<div
				className={cn("flex", orientation === "horizontal" ? "-ml-4 h-full" : "-mt-4 flex-col", className)}
				{...props}
			/>
		</div>
	);
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
	const { orientation } = useCarousel();

	return (
		<div
			role="group"
			aria-roledescription="slide"
			data-slot="carousel-item"
			className={cn(
				"flex-[0_0_100%] min-w-0 overflow-visible",
				orientation === "horizontal" ? "pl-4" : "pt-4",
				className,
			)}
			{...props}
		/>
	);
}

function CarouselPrevious({
	className,
	variant = "outline",
	size = "icon",
	icon,
	...props
}: React.ComponentProps<typeof Button> & { icon?: React.ReactNode }) {
	const { orientation, scrollPrev, canScrollPrev, focusCarousel } = useCarousel();

	const handleClick = React.useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			scrollPrev();
			// Focus the carousel after navigation to maintain keyboard navigation
			setTimeout(() => focusCarousel(), 0);
		},
		[scrollPrev, focusCarousel],
	);

	return (
		<Button
			data-slot="carousel-previous"
			variant={variant}
			size={size}
			className={cn(
				"absolute size-8 rounded-full",
				orientation === "horizontal"
					? "top-[calc(50%-1rem)] left-4 z-10"
					: "-top-12 left-1/2 -translate-x-1/2 rotate-90",
				className,
			)}
			isDisabled={!canScrollPrev}
			onClick={handleClick}
			{...props}
		>
			{icon ? icon : <ChevronLeft className="text-white w-4 h-4" />}
			<span className="sr-only">Previous slide</span>
		</Button>
	);
}

function CarouselNext({
	className,
	variant = "outline",
	size = "icon",
	icon,
	...props
}: React.ComponentProps<typeof Button> & { icon?: React.ReactNode }) {
	const { orientation, scrollNext, canScrollNext, focusCarousel } = useCarousel();

	const handleClick = React.useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			scrollNext();
			// Focus the carousel after navigation to maintain keyboard navigation
			setTimeout(() => focusCarousel(), 0);
		},
		[scrollNext, focusCarousel],
	);

	return (
		<Button
			data-slot="carousel-next"
			variant={variant}
			size={size}
			className={cn(
				"absolute size-8 rounded-full",
				orientation === "horizontal"
					? "top-[calc(50%-1rem)] right-4 z-10"
					: "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
				className,
			)}
			isDisabled={!canScrollNext}
			onClick={handleClick}
			{...props}
		>
			{icon ? icon : <ChevronRight className="text-white w-4 h-4" />}
			<span className="sr-only">Next slide</span>
		</Button>
	);
}

// Memoized exports for better performance
const MemoizedCarousel = React.memo(Carousel);
const MemoizedCarouselContent = React.memo(CarouselContent);
const MemoizedCarouselItem = React.memo(CarouselItem);
const MemoizedCarouselNext = React.memo(CarouselNext);
const MemoizedCarouselPrevious = React.memo(CarouselPrevious);

export {
	MemoizedCarousel as Carousel,
	MemoizedCarouselContent as CarouselContent,
	MemoizedCarouselItem as CarouselItem,
	MemoizedCarouselNext as CarouselNext,
	// eslint-disable-next-line prettier/prettier
	MemoizedCarouselPrevious as CarouselPrevious,
};
